import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  SCHEDULE_WEEKDAY_HEADER_LABELS,
  addWeeks,
  isoDate,
  scheduleDayHeaderLabel,
  scheduleDayTitle,
  startOfWeek,
  weekDatesFromStart,
} from '../../../sampling/surveyScheduleCalendar';

type Props = {
  open: boolean;
  pointLabel: string;
  submitting?: boolean;
  error?: string | null;
  /**
   * Calendar dates (YYYY-MM-DD) already booked for this quotation / work order.
   * Those days stay disabled — sampling is still running on the original schedule.
   */
  blockedDates?: string[];
  onClose: () => void;
  onSubmit: (payload: { proposedDate: string; reason: string }) => void;
};

export function SurveyRescheduleProposeDialog({
  open,
  pointLabel,
  submitting = false,
  error = null,
  blockedDates = [],
  onClose,
  onSubmit,
}: Props) {
  const today = isoDate(new Date());
  const [weekStart, setWeekStart] = useState(() => startOfWeek(today));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const weekDates = useMemo(() => weekDatesFromStart(weekStart), [weekStart]);
  const blockedSet = useMemo(
    () => new Set(blockedDates.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))),
    [blockedDates],
  );

  function handleOpenReset() {
    setWeekStart(startOfWeek(today));
    setSelectedDate(null);
    setReason('');
    setLocalError(null);
  }

  function dateDisabledReason(date: string): string | null {
    if (date <= today) return 'Hanya tanggal yang akan datang yang dapat dipilih.';
    if (blockedSet.has(date)) {
      return 'Tanggal ini sudah dijadwalkan untuk quotation/WO ini (sampling sedang berjalan).';
    }
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEnter: handleOpenReset }}
      data-testid="survey-reschedule-propose-dialog"
    >
      <DialogTitle>Reschedule {pointLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Pilih tanggal kesepakatan customer di luar jadwal yang sedang berjalan. Petugas masih
            kosong — admin sampling akan menentukan ulang tanggal final dan petugas.
          </Typography>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <IconButton
              aria-label="Minggu sebelumnya"
              size="small"
              onClick={() => setWeekStart((w) => addWeeks(w, -1))}
              disabled={submitting}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle2">
              {scheduleDayHeaderLabel(weekDates[0] ?? weekStart)} –{' '}
              {scheduleDayHeaderLabel(weekDates[6] ?? weekStart)}
            </Typography>
            <IconButton
              aria-label="Minggu berikutnya"
              size="small"
              onClick={() => setWeekStart((w) => addWeeks(w, 1))}
              disabled={submitting}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.75,
            }}
          >
            {SCHEDULE_WEEKDAY_HEADER_LABELS.map((label) => (
              <Typography
                key={label}
                variant="caption"
                color="text.secondary"
                align="center"
                sx={{ fontWeight: 600 }}
              >
                {label}
              </Typography>
            ))}
            {weekDates.map((date) => {
              const selected = selectedDate === date;
              const disabledReason = dateDisabledReason(date);
              const disabled = submitting || Boolean(disabledReason);
              const button = (
                <Button
                  size="small"
                  variant={selected ? 'contained' : 'outlined'}
                  color={selected ? 'primary' : 'inherit'}
                  disabled={disabled}
                  onClick={() => {
                    setSelectedDate(date);
                    setLocalError(null);
                  }}
                  data-testid={`survey-reschedule-day-${date}`}
                  sx={{ minWidth: 0, px: 0.5, py: 1.25, width: '100%' }}
                >
                  {date.slice(8, 10)}
                </Button>
              );
              if (!disabledReason) return <React.Fragment key={date}>{button}</React.Fragment>;
              return (
                <Tooltip key={date} title={disabledReason}>
                  <span>{button}</span>
                </Tooltip>
              );
            })}
          </Box>

          {selectedDate ? (
            <Typography variant="body2">Tanggal dipilih: {scheduleDayTitle(selectedDate)}</Typography>
          ) : null}

          <TextField
            label="Catatan (opsional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            multiline
            minRows={2}
            fullWidth
          />

          {localError || error ? <Alert severity="error">{localError || error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Batal
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          data-testid="survey-reschedule-submit"
          onClick={() => {
            if (!selectedDate) {
              setLocalError('Pilih tanggal reschedule terlebih dahulu.');
              return;
            }
            const reasonBlocked = dateDisabledReason(selectedDate);
            if (reasonBlocked) {
              setLocalError(reasonBlocked);
              return;
            }
            onSubmit({ proposedDate: selectedDate, reason: reason.trim() });
          }}
        >
          {submitting ? 'Mengirim…' : 'Ajukan reschedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
